import React, { useState } from 'react';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { USER_ROLES } from '../../config/userRoles';
import { AlertBanner, Button } from '../ui';
import { authStorage } from '../../services/storageService';

// Auth modes
const AUTH_MODE = {
  PASSWORD: 'password',
  MAGIC_LINK: 'magic_link',
  REGISTER: 'register'
};

/**
 * AuthModal - Reusable Authentication Modal
 * 
 * Supports 3 modes:
 * - Password Login (for existing users)
 * - Magic Link (passwordless login)
 * - Register (magic link for new users)
 * 
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Callback when modal closes
 * @param {string} redirectAfterLogin - Optional redirect URL after successful login
 * @param {string} initialInviteCode - Pre-filled invite code for course joining
 */
export function AuthModal({ isOpen, onClose, redirectAfterLogin, initialInviteCode = '' }) {
  const { loginWithPassword, sendMagicLink, loading: authLoading, error: authContextError, clearError } = useUnifiedAuth();
  
  const [mode, setMode] = useState(AUTH_MODE.MAGIC_LINK);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(initialInviteCode);
  const [authError, setAuthError] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      await loginWithPassword(email, password);
      onClose();
    } catch (err) {
      setAuthError(err.message || 'Fehler beim Login');
    }
  };

  const handleMagicLinkSend = async (e) => {
    e.preventDefault();
    setAuthError('');
    setSending(true);
    
    try {
      // Build redirect URL with invite code if provided
      let redirect = redirectAfterLogin;
      if (inviteCode && inviteCode.trim()) {
        redirect = `/student/dashboard?joinCode=${inviteCode.trim().toUpperCase()}`;
      }
      
      // Build action code settings with redirect
      const actionCodeSettings = redirect 
        ? {
            url: `${window.location.origin}/auth/complete-login?redirect=${encodeURIComponent(redirect)}`,
            handleCodeInApp: true
          }
        : undefined;
      
      await sendMagicLink(email, USER_ROLES.STUDENT, actionCodeSettings);
      
      // Store invite code in localStorage for after login
      if (inviteCode && inviteCode.trim()) {
        authStorage.set('pendingInviteCode', inviteCode.trim().toUpperCase());
      }
      
      setLinkSent(true);
    } catch (err) {
      setAuthError(err.message || 'Fehler beim Senden des Login-Links');
    } finally {
      setSending(false);
    }
  };

  const resetModal = () => {
    setEmail('');
    setPassword('');
    setAuthError('');
    setLinkSent(false);
    setSending(false);
    clearError();
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setAuthError('');
    setLinkSent(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 theme-transition">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            {mode === AUTH_MODE.REGISTER ? '📝 Registrieren' : '🔐 Anmelden'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mode Tabs */}
        <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => switchMode(AUTH_MODE.PASSWORD)}
            className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium theme-transition ${
              mode === AUTH_MODE.PASSWORD
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Passwort
          </button>
          <button
            onClick={() => switchMode(AUTH_MODE.MAGIC_LINK)}
            className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium theme-transition ${
              mode === AUTH_MODE.MAGIC_LINK
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Magic Link
          </button>
          <button
            onClick={() => switchMode(AUTH_MODE.REGISTER)}
            className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium theme-transition ${
              mode === AUTH_MODE.REGISTER
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Registrieren
          </button>
        </div>

        {/* Error */}
        {authError && (
          <AlertBanner
            variant="danger"
            message={authError}
            className="mb-4"
          />
        )}

        {/* Content */}
        <div className="min-h-[260px]">
          {linkSent ? (
            /* Magic Link/Register Success */
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                E-Mail gesendet!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Klicke auf den Link in der E-Mail an <strong>{email}</strong>
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400 mb-4">
                💡 Prüfe auch deinen Spam-Ordner
              </p>
              <button
                onClick={() => setLinkSent(false)}
                className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
              >
                Andere E-Mail verwenden
              </button>
            </div>
          ) : mode === AUTH_MODE.PASSWORD ? (
            /* Password Login Form */
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  E-Mail
                </label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-primary-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Passwort
                </label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-primary-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={authLoading}
                loading={authLoading}
              >
                Anmelden
              </Button>
            </form>
          ) : mode === AUTH_MODE.MAGIC_LINK ? (
            /* Magic Link Form */
            <form onSubmit={handleMagicLinkSend} className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Gib deine E-Mail-Adresse ein. Wir senden dir einen Login-Link – 
                <strong className="text-gray-900 dark:text-white"> kein Passwort nötig!</strong>
              </p>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  E-Mail
                </label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-primary-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                  autoFocus
                />
              </div>
              
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={sending || !email}
                loading={sending}
              >
                {sending ? 'Sende...' : 'Magic Link senden'}
              </Button>
            </form>
          ) : (
            /* Register Form (Magic Link) */
            <form onSubmit={handleMagicLinkSend} className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Registriere dich mit deiner E-Mail-Adresse. Du erhältst einen Link zum Erstellen deines Accounts.
              </p>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  E-Mail
                </label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-primary-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Einladungscode <span className="text-gray-500 dark:text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="z.B. ABC123"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-primary-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white uppercase"
                  maxLength={8}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Hast du einen Kurs-Einladungscode? Gib ihn hier ein, um dem Kurs automatisch beizutreten.
                </p>
              </div>
              
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={sending || !email}
                loading={sending}
              >
                {sending ? 'Sende...' : 'Registrieren'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
